<template>
  <lb-dialog
    v-if="internalVisible"
    v-model="internalVisible"
    :width="dialogWidth"
    :heading="dialogHeading"
    persistent
    data-testid="revenue-commentary-drilldown-dialog"
  >
    <template v-slot:body class="pa-4">
      <div class="drilldown-card" data-testid="revenue-commentary-drilldown-dialog__card">
        <!-- Status Chip -->
        <div class="d-flex align-center mb-3" data-testid="revenue-commentary-drilldown-dialog__status-row">
          <StatusIndicator
            :variant="commentaryStatusVariant"
            :custom-label="commentaryStatusLabel"
            outlined
            size="sm"
            data-testid="revenue-commentary-drilldown-dialog__status-indicator"
          />
          <v-spacer></v-spacer>
          <div v-if="isEditMode" data-testid="revenue-commentary-drilldown-dialog__save-state">
            <span class="success--text" v-if="isSaved">Saved</span>
            <span class="error--text" v-else>Unsaved</span>
          </div>
        </div>

        <div class="dialog-content" data-testid="revenue-commentary-drilldown-dialog__body">
          <!-- Summary Card -->
          <section
            class="summary-card d-flex flex-column gap-3"
            data-testid="sl-breakup__summary-card"
          >
            <div
              class="summary-card__header"
              role="button"
              tabindex="0"
              :aria-expanded="isSummaryExpanded.toString()"
              aria-controls="sl-breakup__summary-content"
              data-testid="sl-breakup__summary-toggle"
              @click="toggleSummaryAccordion"
              @keyup.enter.prevent="toggleSummaryAccordion"
              @keyup.space.prevent="toggleSummaryAccordion"
            >
              <h4 class="summary-card__title">Variance analysis summary</h4>
              <v-icon small :class="['summary-toggle-icon', { 'is-expanded': isSummaryExpanded }]">
                {{ isSummaryExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </div>
            <v-expand-transition>
              <div
                v-if="isSummaryExpanded"
                class="summary-card__body d-flex flex-column gap-3"
                id="sl-breakup__summary-content"
                data-testid="sl-breakup__summary-content"
              >
                <template v-if="summaryData">
                  <v-divider></v-divider>
                  <div :class="summaryGridClass">
                    <div class="summary-panel">
                      <h5 class="summary-panel__title">
                        {{ summaryData.actualLabel }} Vs {{ summaryData.proformaLabel }}
                      </h5>
                      <CrossedMaterialityLegend :show-labels="false" />
                      <SummaryMetrics
                        :actualLabel="summaryData.actualLabel"
                        :compareLabel="summaryData.proformaLabel"
                        :actualAmount="summaryData.actualAmount"
                        :compareAmount="summaryData.proformaAmount"
                        :varianceAmount="summaryData.proformaVarianceAmount"
                        :variancePercent="summaryData.proformaVariancePercent"
                        :formatCurrency="formatCurrency"
                        :formatPercent="formatPercent"
                        :varianceClass="varianceClass"
                      />
                    </div>
                    <div class="summary-panel">
                      <h5 class="summary-panel__title">
                        {{ summaryData.actualLabel }} Vs {{ summaryData.forecastLabel }}
                      </h5>
                      <CrossedMaterialityLegend :show-labels="false" />
                      <SummaryMetrics
                        :actualLabel="summaryData.actualLabel"
                        :compareLabel="summaryData.forecastLabel"
                        :actualAmount="summaryData.actualAmount"
                        :compareAmount="summaryData.forecastAmount"
                        :varianceAmount="summaryData.forecastVarianceAmount"
                        :variancePercent="summaryData.forecastVariancePercent"
                        :formatCurrency="formatCurrency"
                        :formatPercent="formatPercent"
                        :varianceClass="varianceClass"
                      />
                    </div>
                  </div>
                </template>
              </div>
            </v-expand-transition>
          </section>

          <!-- Local Commentary Section -->
          <div class="commentary-sections d-flex gap-4" data-testid="revenue-commentary-drilldown-dialog__commentary-sections">
            <!-- Vs Proforma Column -->
            <div class="d-flex flex-column gap-3" style="width: 50%">
              <div class="accordion-section" data-testid="revenue-commentary-drilldown-dialog__local-proforma-section">
                <div
                  class="accordion-header d-flex align-center pa-2 cursor-pointer"
                  @click="localCommentaryProformaExpanded = !localCommentaryProformaExpanded"
                  data-testid="revenue-commentary-drilldown-dialog__local-proforma-header"
                >
                  <span class="font-weight-semibold">{{ commentary_local_key_label }} Commentary (Vs Proforma)</span>
                  <v-spacer></v-spacer>
                  <div
                    class="d-flex align-center mr-2"
                    v-if="showGenAiButton && canEditLocalCommentary"
                  >
                    <v-btn
                      color="primary"
                      small
                      :disabled="isTyping || aiLoadingProforma || aiLoadingForecast || isThresholdExceedCommentary"
                      @click.stop="handleGenerateAISection('One', 'proforma')"
                      data-testid="revenue-commentary-drilldown-dialog__gen-ai-proforma"
                    >
                      <v-icon dark left small>mdi-creation</v-icon>
                      <span class="noTransform">Gen AI</span>
                    </v-btn>
                  </div>
                  <v-icon class="mr-2" size="20">{{ localCommentaryProformaExpanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                </div>
                <v-expand-transition>
                  <div v-show="localCommentaryProformaExpanded" class="accordion-content pa-3">
                    <lb-textarea
                      v-if="canEditLocalCommentary"
                      :hidedetails="false"
                      v-model="internalRowData.commentary1"
                      :label="isLocalProformaMandatory ? commentary_local_key_label + ' Commentary Mandatory' : commentary_local_key_label + ' Commentary Optional'"
                      :required="isLocalProformaMandatory"
                      data-testid="revenue-commentary-drilldown-dialog__local-commentary-proforma"
                    />
                    <div v-else>
                      <div v-if="internalRowData.commentary1" class="commentary-text">{{ internalRowData.commentary1 }}</div>
                      <div class="grey--text" v-else>No Data</div>
                    </div>
                  </div>
                </v-expand-transition>
              </div>

              <!-- Regional Commentary Vs Proforma -->
              <div class="accordion-section" v-if="showRegionalCommentary" data-testid="revenue-commentary-drilldown-dialog__regional-proforma-section">
                <div
                  class="accordion-header d-flex align-center pa-2 cursor-pointer"
                  @click="regionalCommentaryProformaExpanded = !regionalCommentaryProformaExpanded"
                  data-testid="revenue-commentary-drilldown-dialog__regional-proforma-header"
                >
                  <span class="font-weight-semibold">{{ commentary_regional_key_label }} Commentary (Vs Proforma)</span>
                  <v-spacer></v-spacer>
                  <v-icon class="mr-2" size="20">{{ regionalCommentaryProformaExpanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                </div>
                <v-expand-transition>
                  <div v-show="regionalCommentaryProformaExpanded" class="accordion-content pa-3">
                    <lb-textarea
                      v-if="canEditRegionalCommentary"
                      :hidedetails="false"
                      v-model="internalRowData.regionalcommentary1"
                      :label="isRegionalProformaMandatory ? commentary_regional_key_label + ' Commentary Mandatory' : commentary_regional_key_label + ' Commentary Optional'"
                      :required="isRegionalProformaMandatory"
                      data-testid="revenue-commentary-drilldown-dialog__regional-commentary-proforma"
                    />
                    <div v-else>
                      <div v-if="internalRowData.regionalcommentary1" class="commentary-text">{{ internalRowData.regionalcommentary1 }}</div>
                      <div class="grey--text" v-else>No Data</div>
                    </div>
                  </div>
                </v-expand-transition>
              </div>
            </div>

            <!-- Vs Forecast Column -->
            <div class="d-flex flex-column gap-3" style="width: 50%">
              <div class="accordion-section" data-testid="revenue-commentary-drilldown-dialog__local-forecast-section">
                <div
                  class="accordion-header d-flex align-center pa-2 cursor-pointer"
                  @click="localCommentaryForecastExpanded = !localCommentaryForecastExpanded"
                  data-testid="revenue-commentary-drilldown-dialog__local-forecast-header"
                >
                  <span class="font-weight-semibold">{{ commentary_local_key_label }} Commentary (Vs Forecast)</span>
                  <v-spacer></v-spacer>
                  <div
                    class="d-flex align-center mr-2"
                    v-if="showGenAiButton && canEditLocalCommentary"
                  >
                    <v-btn
                      color="primary"
                      small
                      :disabled="isTyping || aiLoadingProforma || aiLoadingForecast || isThresholdExceedCommentary"
                      @click.stop="handleGenerateAISection('Two', 'forecast')"
                      data-testid="revenue-commentary-drilldown-dialog__gen-ai-forecast"
                    >
                      <v-icon dark left small>mdi-creation</v-icon>
                      <span class="noTransform">Gen AI</span>
                    </v-btn>
                  </div>
                  <v-icon class="mr-2" size="20">{{ localCommentaryForecastExpanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                </div>
                <v-expand-transition>
                  <div v-show="localCommentaryForecastExpanded" class="accordion-content pa-3">
                    <lb-textarea
                      v-if="canEditLocalCommentary"
                      :hidedetails="false"
                      v-model="internalRowData.commentary2"
                      :label="isLocalForecastMandatory ? commentary_local_key_label + ' Commentary Mandatory' : commentary_local_key_label + ' Commentary Optional'"
                      :required="isLocalForecastMandatory"
                      data-testid="revenue-commentary-drilldown-dialog__local-commentary-forecast"
                    />
                    <div v-else>
                      <div v-if="internalRowData.commentary2" class="commentary-text">{{ internalRowData.commentary2 }}</div>
                      <div class="grey--text" v-else>No Data</div>
                    </div>
                  </div>
                </v-expand-transition>
              </div>

              <!-- Regional Commentary Vs Forecast -->
              <div class="accordion-section" v-if="showRegionalCommentary" data-testid="revenue-commentary-drilldown-dialog__regional-forecast-section">
                <div
                  class="accordion-header d-flex align-center pa-2 cursor-pointer"
                  @click="regionalCommentaryForecastExpanded = !regionalCommentaryForecastExpanded"
                  data-testid="revenue-commentary-drilldown-dialog__regional-forecast-header"
                >
                  <span class="font-weight-semibold">{{ commentary_regional_key_label }} Commentary (Vs Forecast)</span>
                  <v-spacer></v-spacer>
                  <v-icon class="mr-2" size="20">{{ regionalCommentaryForecastExpanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                </div>
                <v-expand-transition>
                  <div v-show="regionalCommentaryForecastExpanded" class="accordion-content pa-3">
                    <lb-textarea
                      v-if="canEditRegionalCommentary"
                      :hidedetails="false"
                      v-model="internalRowData.regionalcommentary2"
                      :label="isRegionalForecastMandatory ? commentary_regional_key_label + ' Commentary Mandatory' : commentary_regional_key_label + ' Commentary Optional'"
                      :required="isRegionalForecastMandatory"
                      data-testid="revenue-commentary-drilldown-dialog__regional-commentary-forecast"
                    />
                    <div v-else>
                      <div v-if="internalRowData.regionalcommentary2" class="commentary-text">{{ internalRowData.regionalcommentary2 }}</div>
                      <div class="grey--text" v-else>No Data</div>
                    </div>
                  </div>
                </v-expand-transition>
              </div>
            </div>
          </div>

          <!-- Approval/Rejection Reason Section -->
          <div v-if="showApprovalReasonSection" class="approval-section" data-testid="revenue-commentary-drilldown-dialog__approval-section">
            <div class="accordion-section">
              <div
                class="accordion-header d-flex align-center pa-2 cursor-pointer"
                @click="approvalReasonExpanded = !approvalReasonExpanded"
                data-testid="revenue-commentary-drilldown-dialog__approval-reason-header"
              >
                <span class="font-weight-semibold">{{ approvalSectionLabel }} Approval/Rejection Reason</span>
                <v-spacer></v-spacer>
                <v-icon class="mr-2" size="20">{{ approvalReasonExpanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
              </div>
              <v-expand-transition>
                <div v-show="approvalReasonExpanded" class="accordion-content pa-3">
                  <lb-textarea
                    v-if="canEditApprovalReason"
                    :hidedetails="false"
                    v-model="internalRowData.approvereason"
                    :label="approvalSectionLabel + ' Approval/Reject Reason'"
                    data-testid="revenue-commentary-drilldown-dialog__approval-reason"
                  />
                  <div v-else>
                    <div v-if="internalRowData.approvereason || internalRowData.rejectreason" class="commentary-text">{{ internalRowData.stage_local === -1 ? (internalRowData.rejectreason || internalRowData.approvereason) : (internalRowData.approvereason || internalRowData.rejectreason) }}</div>
                    <div class="grey--text" v-else>No Data</div>
                  </div>
                </div>
              </v-expand-transition>
            </div>
          </div>
        </div>

        <!-- Dialog Actions -->
        <div class="dialog-actions" data-testid="revenue-commentary-drilldown-dialog__footer">
          <v-spacer></v-spacer>
          <v-btn
            text
            class="secondaryBtn"
            data-testid="revenue-commentary-drilldown-dialog__close-action"
            @click="handleClose"
          >
            {{ $nova.labelsName('cycle_page', 'close_button_label', 'Close') }}
          </v-btn>
          <v-btn
            v-if="showSaveButton"
            text
            class="primary"
            :loading="saving"
            data-testid="revenue-commentary-drilldown-dialog__save-action"
            @click="handleSave"
          >
            {{ $nova.labelsName('cycle_page', 'save_button_label', 'Save') }}
          </v-btn>
          <v-btn
            v-if="showSubmitButton"
            text
            class="primary"
            :loading="submitting"
            data-testid="revenue-commentary-drilldown-dialog__submit-action"
            @click="handleSubmit"
          >
            {{ submitButtonLabel }}
          </v-btn>
        </div>
      </div>
    </template>
  </lb-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import CrossedMaterialityLegend from './CrossedMaterialityLegend.vue';
import SummaryMetrics from './SummaryMetrics.vue';
import StatusIndicator from './StatusIndicator.vue';
import { STATUS_VARIANTS, COMMENTARY_STAGE_LABEL_MAP, COMMENTARY_STAGE_DEFAULT } from '../utils/statusConstants';

const COMPONENT_PREFIX = '[RevenueCommentaryDrilldownDialog]';

export default {
  name: 'RevenueCommentaryDrilldownDialog',
  components: {
    CrossedMaterialityLegend,
    SummaryMetrics,
    StatusIndicator,
  },
  props: {
    visible: { type: Boolean, default: false },
    rowData: { type: Object, default: () => ({}) },
    title: { type: String, default: '' },
    type: {
      type: String,
      default: 'revenue',
      validator: (value) => ['revenue', 'netsales'].includes(value),
    },
    actualLabel: { type: String, default: 'Actual' },
    proformaLabel: { type: String, default: 'Proforma' },
    forecastLabel: { type: String, default: 'Forecast' },
    // Role props
    ispreparer: { type: Boolean, default: false },
    isapprover: { type: Boolean, default: false },
    isregionalapprover: { type: Boolean, default: false },
    isviewonly: { type: Boolean, default: false },
    // Labels
    commentary_local_key_label: { type: String, default: 'Local' },
    commentary_regional_key_label: { type: String, default: 'Regional' },
    // Workflow props
    workflows: {
      type: Object,
      default: () => ({
        lcp: true, lmp: true, rcp: true, rmp: true,
        lcf: true, lmf: true, rcf: true, rmf: true,
        islocalenabled: true, isregionalenabled: true,
      }),
    },
    workflowtext: { type: Array, default: () => [] },
    cyclestage: { type: Number, default: 0 },
  },
  data() {
    return {
      internalVisible: this.visible,
      internalRowData: this.cloneRowData(this.rowData),
      isSummaryExpanded: true,
      // Commentary accordion states
      localCommentaryProformaExpanded: true,
      localCommentaryForecastExpanded: true,
      regionalCommentaryProformaExpanded: true,
      regionalCommentaryForecastExpanded: true,
      approvalReasonExpanded: true,
      // Save/Submit state
      saving: false,
      submitting: false,
      // Track original values for saved/unsaved state
      originalCommentary1: '',
      originalCommentary2: '',
    };
  },
  computed: {
    ...mapGetters({
      axiosInstance: 'axiosObject',
      isGlobalAiEnabled: 'isAiEnabled',
    }),
    ...mapGetters('varianceanalysis', {
      revenueAiProformaState: 'getRevenueAiProforma',
      revenueAiForecastState: 'getRevenueAiForecast',
      revenueAiLoadingProformaState: 'getRevenueAiLoadingProforma',
      revenueAiLoadingForecastState: 'getRevenueAiLoadingForecast',
      revenueAiTypingState: 'getRevenueAiTyping',
      revenueThresholdState: 'getRevenueCommentaryThresholdStatus',
      revenueGenAiFlagState: 'getRevenueCommentaryGenAiFlag',
    }),
    dialogWidth() {
      return '900';
    },
    dialogHeading() {
      const code = this.internalRowData?.__code || this.internalRowData?.code || '';
      const name = this.internalRowData?.name || this.internalRowData?.__name || '';
      if (code && name) {
        return `${code} - ${name}`;
      }
      if (code) {
        return code;
      }
      return 'Drill-down Commentary';
    },
    dialogTitle() {
      if (this.title) {
        return this.title;
      }
      if (this.$nova && typeof this.$nova.labelsName === 'function') {
        return this.$nova.labelsName('cycle_page', 'commentary_drilldown_title', 'Commentary Drill-Down');
      }
      return 'Commentary Drill-Down';
    },
    normalizedRowData() {
      return this.internalRowData;
    },
    rowStage() {
      return this.internalRowData?.__stage ?? this.internalRowData?.stage ?? null;
    },
    commentaryStatusVariant() {
      const stage = this.rowStage;
      if (stage === -1) return STATUS_VARIANTS.LOCAL_REJECTED;
      if (stage === 0) return STATUS_VARIANTS.LOCAL_AWAITED;
      if (stage === 1) return STATUS_VARIANTS.LOCAL_PENDING;
      if (stage === -2) return STATUS_VARIANTS.REGIONAL_REJECTED;
      if (stage === 5) return STATUS_VARIANTS.REGIONAL_AWAITED;
      if (stage === 6) return STATUS_VARIANTS.REGIONAL_PENDING;
      if (stage === 7) return STATUS_VARIANTS.COMMENTARY_APPROVED; // Or APPROVED
      if (stage === 3) return STATUS_VARIANTS.COMMENTARY_NIL;
      if (stage === 4) return STATUS_VARIANTS.COMMENTARY_IMMATERIAL;
      return STATUS_VARIANTS.UNKNOWN;
    },
    commentaryStatusLabel() {
      const stage = this.rowStage;
      const config = COMMENTARY_STAGE_LABEL_MAP[String(stage)] || COMMENTARY_STAGE_DEFAULT;
      if (config.labelNamespace && config.labelKey) {
        return this.$nova.labelsName(config.labelNamespace, config.labelKey, config.fallback);
      }
      return config.fallback || '';
    },
    rowAction() {
      return this.internalRowData?.__myaction || '';
    },
    summaryData() {
      const source = this.normalizedRowData;
      if (!source || Object.keys(source).length === 0) {
        return null;
      }

      const actualLabel = source.amount1heading || this.actualLabel;
      const proformaLabel = source.amount2heading || this.proformaLabel;
      const forecastLabel = source.amount3heading || this.forecastLabel;

      return {
        actualLabel,
        proformaLabel,
        forecastLabel,
        actualAmount: source.amount1 ?? 0,
        proformaAmount: source.amount2 ?? 0,
        forecastAmount: source.amount3 ?? 0,
        proformaVarianceAmount: source.varianceamount1 ?? 0,
        proformaVariancePercent: source.variancepercent1 ?? 0,
        forecastVarianceAmount: source.varianceamount2 ?? 0,
        forecastVariancePercent: source.variancepercent2 ?? 0,
      };
    },
    summaryGridClass() {
      return this.type === 'netsales' ? 'netsales-summary__grid' : 'revenue-summary__grid';
    },
    // Edit mode checks
    isEditMode() {
      const action = this.rowAction;
      return ['localadd', 'localvoluntary', 'regionaladd', 'regionalvoluntary', 'localapprove', 'regionalapprove'].includes(action);
    },
    canEditLocalCommentary() {
      const action = this.rowAction;
      return ['localadd', 'localvoluntary'].includes(action);
    },
    canEditRegionalCommentary() {
      const action = this.rowAction;
      return ['regionaladd', 'regionalvoluntary', 'approveregional'].includes(action);
    },
    canEditApprovalReason() {
      const action = this.rowAction;
      return ['localapprove', 'regionalapprove'].includes(action);
    },
    // Show/hide sections
    showRegionalCommentary() {
      // Show regional commentary for stages 5, 6, 7 or if user is approver/regional approver
      const stage = this.rowStage;
      return stage >= 5 || this.isapprover || this.isregionalapprover;
    },
    showApprovalReasonSection() {
      const action = this.rowAction;
      const stage = this.rowStage;
      // Show for approve actions or when there's an approval/rejection to display
      return ['localapprove', 'regionalapprove'].includes(action) || 
             stage === 1 || stage === 6 || stage === 7 || stage === -1 || stage === -2;
    },
    approvalSectionLabel() {
      const action = this.rowAction;
      if (action === 'regionalapprove' || this.rowStage >= 5) {
        return this.commentary_regional_key_label;
      }
      return this.commentary_local_key_label;
    },
    // Mandatory checks
    isLocalProformaMandatory() {
      const row = this.internalRowData || {};
      return Boolean(row.var1_cross_control || row.var1_cross_management);
    },
    isLocalForecastMandatory() {
      const row = this.internalRowData || {};
      return Boolean(row.var2_cross_control || row.var2_cross_management);
    },
    isRegionalProformaMandatory() {
      const row = this.internalRowData || {};
      return Boolean(row.var1_cross_control_regional || row.var1_cross_management_regional);
    },
    isRegionalForecastMandatory() {
      const row = this.internalRowData || {};
      return Boolean(row.var2_cross_control_regional || row.var2_cross_management_regional);
    },
    // Button visibility
    showSaveButton() {
      return this.canEditLocalCommentary || this.canEditRegionalCommentary;
    },
    showSubmitButton() {
      const action = this.rowAction;
      return ['localadd', 'localvoluntary', 'regionaladd', 'regionalvoluntary', 'localapprove', 'regionalapprove'].includes(action);
    },
    submitButtonLabel() {
      const action = this.rowAction;
      if (['localapprove', 'regionalapprove'].includes(action)) {
        return this.$nova.labelsName('cycle_page', 'approve_button_label', 'Approve');
      }
      return this.$nova.labelsName('cycle_page', 'submit_button_label', 'Submit');
    },
    // Saved state
    isSaved() {
      return this.originalCommentary1 === this.internalRowData?.commentary1 &&
             this.originalCommentary2 === this.internalRowData?.commentary2;
    },
    // AI related
    aiProforma: {
      get() {
        return this.revenueAiProformaState || '';
      },
      set(value) {
        this.updateRevenueAiState({ revenueAIProforma: value }).catch((error) => {
          console.error(`${COMPONENT_PREFIX}: updateRevenueAiState proforma`, error);
        });
      },
    },
    aiForecast: {
      get() {
        return this.revenueAiForecastState || '';
      },
      set(value) {
        this.updateRevenueAiState({ revenueAIForecast: value }).catch((error) => {
          console.error(`${COMPONENT_PREFIX}: updateRevenueAiState forecast`, error);
        });
      },
    },
    aiLoadingProforma() {
      return !!this.revenueAiLoadingProformaState;
    },
    aiLoadingForecast() {
      return !!this.revenueAiLoadingForecastState;
    },
    isTyping() {
      return !!this.revenueAiTypingState;
    },
    isThresholdExceedCommentary() {
      return !!this.revenueThresholdState;
    },
    commentaryGenAiBtntab() {
      return !!this.revenueGenAiFlagState;
    },
    isGenAiEnabled() {
      return this.isGlobalAiEnabled && this.commentaryGenAiBtntab;
    },
    showGenAiButton() {
      return this.$store.state.aiEnabled && this.commentaryGenAiBtntab;
    },
  },
  watch: {
    visible(next) {
      if (next !== this.internalVisible) {
        this.internalVisible = next;
      }
      if (next) {
        this.internalRowData = this.cloneRowData(this.rowData);
        this.originalCommentary1 = this.internalRowData?.commentary1 || '';
        this.originalCommentary2 = this.internalRowData?.commentary2 || '';
        this.isSummaryExpanded = true;
        this.seedAiFields();
      }
    },
    rowData: {
      handler(next) {
        if (!this.internalVisible) {
          return;
        }
        this.internalRowData = this.cloneRowData(next);
        this.originalCommentary1 = this.internalRowData?.commentary1 || '';
        this.originalCommentary2 = this.internalRowData?.commentary2 || '';
        this.seedAiFields();
      },
      deep: true,
    },
    internalVisible(next) {
      if (next === this.visible) {
        if (next) {
          this.internalRowData = this.cloneRowData(this.rowData);
          this.originalCommentary1 = this.internalRowData?.commentary1 || '';
          this.originalCommentary2 = this.internalRowData?.commentary2 || '';
          this.isSummaryExpanded = true;
          this.seedAiFields();
        }
        return;
      }
      this.$emit('update:visible', next);
      if (!next) {
        this.$emit('close');
        this.resetDialogState();
      } else {
        this.internalRowData = this.cloneRowData(this.rowData);
        this.originalCommentary1 = this.internalRowData?.commentary1 || '';
        this.originalCommentary2 = this.internalRowData?.commentary2 || '';
        this.isSummaryExpanded = true;
        this.seedAiFields();
      }
    },
  },
  beforeDestroy() {
    this.resetDialogState();
  },
  methods: {
    ...mapActions('varianceanalysis', {
      generateRevenueCommentaryAi: 'generateRevenueCommentaryAI',
      updateRevenueAiState: 'updateRevenueAiState',
    }),
    cloneRowData(source) {
      if (!source || typeof source !== 'object') {
        return {};
      }
      try {
        return JSON.parse(JSON.stringify(source));
      } catch (error) {
        console.error(`${COMPONENT_PREFIX}: cloneRowData`, error);
        return {};
      }
    },
    seedAiFields() {
      const row = this.normalizedRowData || {};
      this.updateRevenueAiState({
        revenueAIProforma: row.commentary1 || '',
        revenueAIForecast: row.commentary2 || '',
      }).catch((error) => {
        console.error(`${COMPONENT_PREFIX}: seedAiFields`, error);
      });
    },
    toggleSummaryAccordion() {
      this.isSummaryExpanded = !this.isSummaryExpanded;
    },
    async handleGenerateAISection(requestType, variant) {
      try {
        const result = await this.generateRevenueCommentaryAi({
          axios: this.axiosInstance,
          requestType,
          variant,
          rowData: this.normalizedRowData,
        });
        // Update the textarea with generated AI content
        if (result) {
          if (variant === 'proforma' && this.internalRowData) {
            this.internalRowData.commentary1 = result;
          } else if (variant === 'forecast' && this.internalRowData) {
            this.internalRowData.commentary2 = result;
          }
        }
      } catch (error) {
        console.error(`${COMPONENT_PREFIX}: handleGenerateAISection`, error);
      }
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined || amount === '') {
        return '-';
      }
      if (this.$nova && typeof this.$nova.formatCurrency === 'function') {
        return this.$nova.formatCurrency(amount, '', false, true);
      }
      const numericValue = Number(amount);
      return Number.isNaN(numericValue) ? '-' : numericValue.toLocaleString();
    },
    formatPercent(value) {
      if (value === null || value === undefined || value === '') {
        return '-';
      }
      const percent = Number(value) * 100;
      if (Number.isNaN(percent)) {
        return '-';
      }
      if (this.$nova && typeof this.$nova.formatCurrency === 'function') {
        const formatted = this.$nova.formatCurrency(percent, '', false, true);
        return `${formatted}${percent !== 0 ? ' %' : ''}`;
      }
      return `${percent.toFixed(1)}${percent !== 0 ? '%' : ''}`;
    },
    varianceClass(value) {
      if (value > 0) {
        return 'success--text font-weight-bold';
      }
      if (value < 0) {
        return 'error--text font-weight-bold';
      }
      return 'grey--text font-weight-bold';
    },
    handleClose() {
      if (!this.internalVisible) {
        this.$emit('close');
        this.resetDialogState();
        this.$emit('update:visible', false);
        return;
      }
      this.internalVisible = false;
    },
    async handleSave() {
      this.saving = true;
      try {
        this.$emit('save', this.internalRowData);
        this.originalCommentary1 = this.internalRowData?.commentary1 || '';
        this.originalCommentary2 = this.internalRowData?.commentary2 || '';
        this.$store.commit('sbSuccess', 'Commentary saved successfully', { root: true });
      } catch (error) {
        console.error(`${COMPONENT_PREFIX}: handleSave`, error);
        this.$store.commit('sbError', 'Failed to save commentary', { root: true });
      } finally {
        this.saving = false;
      }
    },
    async handleSubmit() {
      this.submitting = true;
      try {
        this.$emit('submit', this.internalRowData);
        this.handleClose();
      } catch (error) {
        console.error(`${COMPONENT_PREFIX}: handleSubmit`, error);
        this.$store.commit('sbError', 'Failed to submit commentary', { root: true });
      } finally {
        this.submitting = false;
      }
    },
    resetDialogState() {
      this.internalRowData = {};
      this.isSummaryExpanded = true;
      this.localCommentaryProformaExpanded = true;
      this.localCommentaryForecastExpanded = true;
      this.regionalCommentaryProformaExpanded = true;
      this.regionalCommentaryForecastExpanded = true;
      this.approvalReasonExpanded = true;
      this.originalCommentary1 = '';
      this.originalCommentary2 = '';
    },
  },
};
</script>

<style scoped>
.drilldown-card {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.dialog-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
}

.dialog-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.dialog-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.summary-card {
  background: #ffffff;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #f7f7f7;
  box-shadow: 4px 4px 20px rgba(0, 0, 0, 0.08);
}

.summary-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
}

.summary-card__header:focus {
  outline: none;
}

.summary-card__header:focus-visible {
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.4);
  border-radius: 8px;
}

.summary-card__title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.summary-card__body {
  width: 100%;
}

.summary-toggle-icon {
  transition: transform 0.2s ease-in-out;
}

.summary-toggle-icon.is-expanded {
  transform: rotate(180deg);
}

.drilldown-genai-section {
  width: 100%;
}

.summary-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.summary-panel__title {
  margin: 0;
  font-weight: 600;
  color: #1f2937;
}

.revenue-summary__grid,
.netsales-summary__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
}

.gap-3 {
  gap: 12px;
}

.gap-4 {
  gap: 16px;
}

/* Commentary Sections */
.commentary-sections {
  width: 100%;
}

/* Accordion Styles */
.accordion-section {
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.accordion-header {
  background: #f9fafb;
  transition: background-color 0.2s ease;
}

.accordion-header:hover {
  background: #f3f4f6;
}

.accordion-header.cursor-pointer {
  cursor: pointer;
}

.accordion-content {
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
}

/* Commentary Text Display */
.commentary-text {
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-size: 14px;
  line-height: 1.5;
  color: #374151;
}

/* Approval Section */
.approval-section {
  width: 100%;
}

/* Utilities */
.noTransform {
  text-transform: none !important;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
